import React, { useContext } from 'react';
import { Button, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { IEntityTerminationParams } from '../common/ParamTypes';
import PivotHeader from '../common/headers/PivotHeader';
import { IPivotHeaderItems } from '../../utils/types/IPivotHeaderItems';
import { iconButtonStyles } from '../../app/common/styles/CommonStyleObjects';
import FeatureFlags from '../../utils/featureFlags/FeatureFlags';
import FeatureFlagContext from '../../utils/featureFlags/FeatureFlagContext';
import hasAccess from '../../utils/authorization/authorizationCheck';
import UserContext from '../../utils/authorization/UserContext';
import { StatUser } from '../../utils/statApi/UsersApi';

interface IEntityTerminationHeaderProps {
  showBackToListButton?: boolean;
  companyCode?: string;
  entityLegalName?: string;
  loading?: boolean;
  baseRoute?: string;
}

const EntityTerminationHeader = (props: IEntityTerminationHeaderProps): JSX.Element => {
  const { showBackToListButton, companyCode, entityLegalName, loading, baseRoute } = props;
  const { companyCode: routeCompanyCode } = useParams<IEntityTerminationParams>();
  const history = useHistory();
  const location = useLocation();
  const user = useContext(UserContext) as StatUser | null;
  const featureFlags: FeatureFlags = React.useContext(FeatureFlagContext);

  // Set the correct pivot tab when navigating back to the Monitoring Queue list from the originating
  // stage passed as a query param by the list page. Defaults to 'All' when absent (e.g. deep links),
  // since the 'overview' segment is shared by multiple stages and can't be disambiguated from the path.
  const terminationStageQueryValue =
    new URLSearchParams(location.search).get('entityTerminationStage') ?? 'All';

  // Form the header text here
  const headerText = `${companyCode} - ${entityLegalName}`;

  const headerItems: IPivotHeaderItems = {
    overview: {
      headerText: 'Overview',
    },
    'eligibility-review': {
      headerText: 'Eligibility Review',
    },
    blocker: {
      headerText: 'Blocker',
    },
    'legal-in-process': {
      headerText: 'Legal In Process',
    },
    'finance-incomplete': {
      headerText: 'Finance Incomplete',
    },
  };

  const showAllHeaderItems =
    featureFlags.flags.EntityTerminationMonitoringQueue &&
    hasAccess(user?.permissions, 'MonitoringQueue:View');

  const renderHeaderTitle = (): JSX.Element => {
    if (!loading && (headerText || routeCompanyCode)) {
      return (
        <h1>
          {showBackToListButton && (
            <Button
              appearance="subtle"
              icon={<ArrowLeftRegular color={iconButtonStyles.color} />}
              style={{ marginRight: 10 }}
              title="Back to entity termination list"
              aria-label="Back to entity termination list"
              // Takes user back to Monitoring Queue under the same termination stage pivot tab
              onClick={(): void => {
                const search = new URLSearchParams();
                search.set('entityTerminationStage', terminationStageQueryValue);
                history.push({ pathname: `/${baseRoute}`, search: `?${search.toString()}` });
              }}
            />
          )}
          {`${headerText}`}
        </h1>
      );
    }
    return (
      <Skeleton aria-label="Loading entity termination header">
        <SkeletonItem />
      </Skeleton>
    );
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <PivotHeader
        getHeaderTitle={renderHeaderTitle}
        pivotHeaderItems={
          showAllHeaderItems
            ? headerItems
            : // If the Monitoring Queue feature flag is off, only show the Eligibility Review pivot
              { 'eligibility-review': headerItems['eligibility-review'] }
        }
        baseUri={`/${baseRoute}/${routeCompanyCode}`}
        ariaLabel="Navigation for Entity Termination Details"
      />
    </div>
  );
};

export default EntityTerminationHeader;
